"use client";

import { useEffect } from "react";
import { cn } from "@heroui/react";

import { Option } from "@/types/dataSchema";

import { setUserAction } from "../helpers/user-actions";

import { useAnswerContext } from "./AnswerContext";
import OtherInputModal from "./OtherInputModal";
import { OptionGrid } from "./components/OptionGrid";
import { OptionActions } from "./components/OptionActions";
import { useOptionSelection } from "./hooks/useOptionSelection";

// Re-export for backward compatibility
export {
  bootstrapIcons,
  getIconFromBoostrapIconKey,
} from "./utils/optionUtils";

interface OptionsProps {
  questionId: string;
  options: Option[];
  isMultiSelect: boolean;
  onSelect: (questionId: string, value: string | string[]) => void;
  currentAnswers?: string | string[];
  isSkippable?: boolean;
  isRevisited?: boolean;
}

export default function Options({
  questionId,
  options,
  isMultiSelect,
  onSelect,
  currentAnswers,
  isSkippable = false,
  isRevisited = false,
}: OptionsProps) {
  const { answers: currentAnswersFromContext } = useAnswerContext();

  const {
    selectedOptions,
    editValues,
    modalOpen,
    modalConfig,
    isRevealed,
    setIsRevealed,
    handleSelectOption,
    handleModalConfirm,
    handleModalClose,
    handleConfirmMultiSelect,
  } = useOptionSelection({
    questionId,
    isMultiSelect,
    currentAnswers,
    options,
    onSelect: (questionId: string, value: string | string[]) => {
      onSelect(questionId, value);
      // Track user action
      setUserAction({
        eventType: "onboarding_answer",
        questionId,
        value,
      });
    },
  });

  useEffect(() => {
    // Trigger reveal animation when options change
    setIsRevealed(false);

    // Skip animation for revisited questions
    if (isRevisited) {
      setIsRevealed(true);

      return;
    }

    const timer = setTimeout(() => setIsRevealed(true), 100);

    return () => clearTimeout(timer);
  }, [options, questionId, isRevisited, setIsRevealed]);

  const handleSkipQuestion = () => {
    // Track skip action
    setUserAction({
      eventType: "onboarding_skip",
      questionId: questionId,
      value: "skipped",
    });

    // Use onSelect with a special value to indicate a skip
    onSelect(questionId, "(skipped)");
  };

  return (
    <div
      className={cn(
        "flex",
        "flex-col",
        "space-y-3 mx-3 transition-all duration-300 ease-out",
        isRevealed ? "opacity-100" : "opacity-0",
      )}
    >
      {isMultiSelect && (
        <p
          className={cn(
            "text-sm text-gray-400 mb-2 transition-all duration-300 ease-out",
            isRevealed
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-2",
          )}
          style={{
            transitionDelay: "100ms",
          }}
        >
          Multiple selections possible
        </p>
      )}

      <OptionGrid
        currentAnswersFromContext={currentAnswersFromContext}
        editValues={editValues}
        isMultiSelect={isMultiSelect}
        isRevealed={isRevealed}
        options={options}
        selectedOptions={selectedOptions}
        onSelectOption={handleSelectOption}
      />

      <OptionActions
        isMultiSelect={isMultiSelect}
        isRevealed={isRevealed}
        isSkippable={isSkippable}
        selectedOptions={selectedOptions}
        onConfirmMultiSelect={handleConfirmMultiSelect}
        onSkipQuestion={handleSkipQuestion}
      />

      {modalConfig && (
        <OtherInputModal
          initialValue={editValues[modalConfig.optionValue] || ""}
          isOpen={modalOpen}
          placeholder={modalConfig.placeholder}
          title={`Please specify your answer for "${modalConfig.label}"`}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
        />
      )}
    </div>
  );
}
